import AIPilot from './AIPilot.js';
import { ENEMIES } from '../core/SimConfig.js';
import { clamp, rand, srand } from '../utils.js';

/**
 * Helicopter AI — simple state machine.
 *
 *   - explore: pick a random world waypoint, fly to it, repeat
 *   - follow:  trail the assigned leader at a fixed offset behind it
 *   - loiter:  (stub) hover in place
 *   - evade:   (stub) flee from threat
 *
 * Each tick sets the flight model's desiredHeading / desiredSpeed /
 * desiredVerticalRate; the flight model handles motion.
 */
export default class HelicopterAIPilot extends AIPilot {
  constructor(params) {
    super();
    this.params = params;
    this.state = 'explore';

    this.waypoint = null;          // { x, z, altAGL }
    this._terrainQueryTimer = 0;
    this._groundHeight = 0;        // last good terrain height (heli + lookahead max)
    this._hasTerrainSample = false;

    this._threat = null;           // future use — set by threaten()

    // Follow-the-leader state
    this.leader = null;            // EnemyHelicopter being trailed
    this._followGap = 0;           // m — desired distance behind leader
    this._followLateral = 0;       // m — desired side offset (+ = leader's right)
    this._followAltOffset = 0;     // m — desired Y offset relative to leader
  }

  /** Enter follow state targeting `leader`. Offsets are randomized within config. */
  followLeader(leader) {
    const f = ENEMIES.formation;
    this.leader = leader;
    this._followGap = f.spacing + srand() * f.spacingJitter;
    this._followLateral = srand() * f.lateralJitter;
    this._followAltOffset = srand() * f.altitudeJitter;
    this.state = 'follow';
  }

  /** Promote this bird to head its own chain (leader was lost). */
  promoteToLeader() {
    this.leader = null;
    this.waypoint = null;          // force fresh waypoint pick on next tick
    this.state = 'explore';
  }

  threaten(threat) {
    this._threat = threat;
    // TODO: transition into 'evade' once that state is implemented.
  }

  update(dt, heli, terrainCollider) {
    this._sampleTerrain(dt, heli, terrainCollider);

    // If our leader is gone or no longer flying (structural failure), promote
    // before this tick so we don't chase a falling wreck.
    if (this.leader && (this.leader.dead || this.leader.flight.structuralFailure)) {
      this.promoteToLeader();
    }

    switch (this.state) {
      case 'follow': this._tickFollow(heli); break;
      case 'loiter': this._tickLoiter(heli); break;
      case 'evade':  this._tickEvade(heli);  break;
      case 'explore':
      default:       this._tickExplore(heli); break;
    }
  }

  // ── Terrain sampling ───────────────────────────────────────────────────
  _sampleTerrain(dt, heli, terrainCollider) {
    this._terrainQueryTimer -= dt;
    if (this._terrainQueryTimer > 0 || !terrainCollider) return;
    this._terrainQueryTimer = ENEMIES.terrainQueryInterval;

    const here = terrainCollider.getHeightAt(heli.position.x, heli.position.z);

    // Look-ahead point along forward heading
    const lookDist = heli.forwardSpeed * ENEMIES.terrainLookaheadSeconds;
    const ax = heli.position.x - Math.sin(heli.heading) * lookDist;
    const az = heli.position.z - Math.cos(heli.heading) * lookDist;
    const ahead = terrainCollider.getHeightAt(ax, az);

    // Use max of (here, ahead) so we start climbing before reaching a hill
    let h = null;
    if (here !== null && ahead !== null) h = Math.max(here, ahead);
    else if (here !== null) h = here;
    else if (ahead !== null) h = ahead;

    if (h !== null) {
      this._groundHeight = h;
      this._hasTerrainSample = true;
    }
    // If null (tile not streamed), keep last known value
  }

  // ── States ─────────────────────────────────────────────────────────────
  _tickExplore(heli) {
    const p = this.params;

    // Pick a new waypoint if needed
    if (!this.waypoint || this._distToWaypointXZ(heli) < ENEMIES.waypointReachRadius) {
      this.waypoint = this._randomWaypoint();
    }

    // Heading toward waypoint
    const dx = this.waypoint.x - heli.position.x;
    const dz = this.waypoint.z - heli.position.z;
    // Forward convention: heading 0 → -Z. atan2(-dx, -dz) maps (dx, dz) → heading.
    heli.desiredHeading = Math.atan2(-dx, -dz);

    // Speed: cruise
    heli.desiredSpeed = p.cruiseSpeed;

    // Altitude — simple P controller toward (groundHeight + waypoint AGL).
    // Falls back to current altitude target if no terrain sample yet (level flight).
    let targetY;
    if (this._hasTerrainSample) {
      targetY = this._groundHeight + this.waypoint.altAGL;
    } else {
      targetY = heli.position.y;
    }
    const altErr = targetY - heli.position.y;
    const desiredVR = clamp(
      altErr * ENEMIES.altitudeP,
      -p.maxDescentRate, p.maxClimbRate,
    );
    heli.desiredVerticalRate = desiredVR;

    // Hard floor — if we're below minAltAGL above ground, force a climb
    if (this._hasTerrainSample) {
      const aglNow = heli.position.y - this._groundHeight;
      if (aglNow < p.minAltAGL) {
        heli.desiredVerticalRate = p.maxClimbRate;
      }
    }
  }

  // Trail the leader at (gap behind, lateral side, altOffset Y) in the
  // leader's body frame. Heading aims at the slot, speed is leader's speed
  // plus a P-correction on distance error, altitude P-tracks the slot Y.
  _tickFollow(heli) {
    const p = this.params;
    const lf = this.leader.flight;

    // Leader-frame basis vectors. Forward = -Z at heading 0.
    const fwdX = -Math.sin(lf.heading);
    const fwdZ = -Math.cos(lf.heading);
    const rightX =  Math.cos(lf.heading);
    const rightZ = -Math.sin(lf.heading);

    // Slot world position: behind leader by gap, offset laterally and vertically.
    const slotX = lf.position.x - fwdX * this._followGap + rightX * this._followLateral;
    const slotZ = lf.position.z - fwdZ * this._followGap + rightZ * this._followLateral;
    const slotY = lf.position.y + this._followAltOffset;

    // Heading toward the slot. Once we're very close, mirror leader heading
    // so the formation flies straight instead of jittering on tiny errors.
    const dx = slotX - heli.position.x;
    const dz = slotZ - heli.position.z;
    const distXZ = Math.sqrt(dx * dx + dz * dz);
    if (distXZ > 5) {
      heli.desiredHeading = Math.atan2(-dx, -dz);
    } else {
      heli.desiredHeading = lf.heading;
    }

    // Speed: leader's forward speed plus P-term on signed longitudinal error.
    // Positive longErr = slot is ahead of us along leader's heading → speed up.
    const f = ENEMIES.formation;
    const longErr = dx * fwdX + dz * fwdZ;
    const speedDelta = clamp(longErr * f.distanceP, -f.maxSpeedDelta, f.maxSpeedDelta);
    heli.desiredSpeed = clamp(lf.forwardSpeed + speedDelta, p.minSpeed, p.maxSpeed);

    // Altitude: P-track the slot Y, but enforce minAltAGL hard floor.
    const altErr = slotY - heli.position.y;
    let desiredVR = clamp(altErr * ENEMIES.altitudeP, -p.maxDescentRate, p.maxClimbRate);
    if (this._hasTerrainSample) {
      const aglNow = heli.position.y - this._groundHeight;
      if (aglNow < p.minAltAGL) desiredVR = p.maxClimbRate;
    }
    heli.desiredVerticalRate = desiredVR;
  }

  _tickLoiter(heli) {
    // Stub: hover at current position with zero forward speed.
    heli.desiredSpeed = 0;
    heli.desiredVerticalRate = 0;
    // Heading unchanged.
  }

  _tickEvade(heli) {
    // Stub: not yet implemented — fall back to explore behavior.
    this._tickExplore(heli);
  }

  // ── Helpers ────────────────────────────────────────────────────────────
  _distToWaypointXZ(heli) {
    if (!this.waypoint) return Infinity;
    const dx = this.waypoint.x - heli.position.x;
    const dz = this.waypoint.z - heli.position.z;
    return Math.sqrt(dx * dx + dz * dz);
  }

  _randomWaypoint() {
    const p = this.params;
    const [minX, minZ] = ENEMIES.worldMin;
    const [maxX, maxZ] = ENEMIES.worldMax;
    const altAGL = clamp(
      p.cruiseAltAGL + rand(-ENEMIES.waypointAltJitter, ENEMIES.waypointAltJitter),
      p.minAltAGL, p.maxAltAGL,
    );
    return {
      x: rand(minX, maxX),
      z: rand(minZ, maxZ),
      altAGL,
    };
  }
}
